<template>
  <div class="mini-kasko-order">
    <Breadcrumbs :breadcrumbs="breadcrumbs"/>

    <MiniKaskoOrderHeader
      :page-title="pageTitle"
      :page-subtitle="pageSubtitle"
      :status-label="statusLabel"
      :status-class="statusClass"
      :formatted-price="formattedPrice"
      :has-earnings="hasEarnings"
      :formatted-earnings="formattedEarnings"
      :earnings-percent="earningsPercent"
    />

    <MiniKaskoOrderFormSection
      :order-id="orderId"
      :is-declared="isDeclared"
      :is-concluded="isConcluded"
      @order-updated="onOrderUpdated"
    />

    <MiniKaskoOtpSection
      v-if="canConfirm"
      :order-id="orderId"
      :price="price"
      :earnings="earnings"
      :company-id="companyId"
      :policy-number="policyNumber"
      :insurant-name="insurantName"
      :customer-code="customerCode"
      :preloaded-otp-state="otpPreloadedState"
      @order-confirmed="onOrderConfirmed"
    />
  </div>
</template>

<script>
import { Breadcrumbs } from '@/shared/ui/breadcrumbs';
import MiniKaskoOrderHeader from '@/views/miniKasko/components/MiniKaskoOrderHeader.vue';
import MiniKaskoOrderFormSection from '@/views/miniKasko/components/MiniKaskoOrderFormSection.vue';
import MiniKaskoOtpSection from '@/views/miniKasko/components/MiniKaskoOtpSection.vue';

export default {
  components: { Breadcrumbs, MiniKaskoOrderHeader, MiniKaskoOrderFormSection, MiniKaskoOtpSection },
  data() {
    return {
      breadcrumbs: [
        { title: 'Панель управління', path: '/prime' },
        { title: 'Міні КАСКО', path: '/mini-kasko-offers' },
        { title: 'Форма замовлення', path: '#' },
      ],
      orderId: this.$route.params.orderId || '',
      isDeclared: false,
      isConcluded: false,
      price: null,
      earnings: null,
      earningsPercent: null,
      policyNumber: '',
      statusId: null,
      showEarnings: false,
      insurantName: '',
      customerCode: '',
      companyId: '',
      otpPreloadedState: null,
    };
  },
  computed: {
    pageTitle() {
      if (this.isConcluded) return 'Міні КАСКО';
      if (this.isDeclared) return 'Заявка Міні КАСКО';
      return 'Оформлення Міні КАСКО';
    },
    pageSubtitle() {
      if (this.isConcluded) return 'Поліс сформовано.';
      if (this.isDeclared) return 'Заявку створено. Перевірте чернетку та підтвердьте оформлення OTP-кодом.';
      return 'Заповніть дані авто та страхувальника для створення чернетки або заявлення полісу.';
    },
    canConfirm() {
      return Boolean(this.orderId && this.isDeclared && !this.isConcluded);
    },
    statusLabel() {
      if (this.isConcluded) return 'Підтверджено';
      if (this.isDeclared) return 'Заявлено';
      if (this.orderId) return 'Чернетка';
      return 'Нове замовлення';
    },
    statusClass() {
      if (this.isConcluded) return 'mini-kasko-order__status--success';
      if (this.isDeclared) return 'mini-kasko-order__status--warning';
      if (this.orderId) return 'mini-kasko-order__status--draft';
      return 'mini-kasko-order__status--new';
    },
    formattedPrice() {
      if (this.price === null || this.price === undefined || Number.isNaN(this.price)) return 'Не вказано';
      return `${this.price} ₴`;
    },
    formattedEarnings() {
      if (this.earnings === null || this.earnings === undefined || Number.isNaN(this.earnings)) return 'Не вказано';
      return `${this.earnings} ₴`;
    },
    hasEarnings() {
      return this.earnings !== null && this.earnings !== undefined && !Number.isNaN(this.earnings);
    },
  },
  watch: {
    '$route.params.orderId'(nextValue) {
      const val = nextValue;
      if (!val || val === 'undefined' || val === 'null') {
        this.orderId = '';
      } else {
        this.orderId = String(val);
      }
    },
  },
  methods: {
    onOrderUpdated(snapshot) {
      if (snapshot.orderId !== undefined) this.orderId = snapshot.orderId;
      if (snapshot.isDeclared !== undefined) this.isDeclared = snapshot.isDeclared;
      if (snapshot.isConcluded !== undefined) this.isConcluded = snapshot.isConcluded;
      if (snapshot.price !== undefined) this.price = snapshot.price;
      if (snapshot.earnings !== undefined) this.earnings = snapshot.earnings;
      if (snapshot.earningsPercent !== undefined) this.earningsPercent = snapshot.earningsPercent;
      if (snapshot.policyNumber !== undefined) this.policyNumber = snapshot.policyNumber;
      if (snapshot.statusId !== undefined) this.statusId = snapshot.statusId;
      if (snapshot.insurantName !== undefined) this.insurantName = snapshot.insurantName;
      if (snapshot.customerCode !== undefined) this.customerCode = snapshot.customerCode;
      if (snapshot.companyId !== undefined) this.companyId = snapshot.companyId;
      if (snapshot.otpPreloadedState !== undefined) this.otpPreloadedState = snapshot.otpPreloadedState;
    },
    onOrderConfirmed() {
      this.isConcluded = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.mini-kasko-order {
  color: #09090b;

  &__alert {
    padding: 12px 14px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    line-height: 20px;

    &--error {
      background: #fef2f2;
      color: #b91c1c;
      border: 1px solid #fecaca;
    }

    &--success {
      background: #f0fdf4;
      color: #166534;
      border: 1px solid #bbf7d0;
    }

    &--warning {
      background: #fffbeb;
      color: #92400e;
      border: 1px solid #fde68a;
    }
  }

  &__card {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &--wide {
      grid-column: span 2;
    }
  }

  &__group-title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: span 2;
    }

    span {
      font-size: 13px;
      line-height: 18px;
      color: #52525b;
    }

    input,
    select {
      width: 100%;
      min-height: 44px;
      padding: 10px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #fff;
      outline: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &:focus {
        border-color: #fb923c;
        box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
      }

      &[readonly],
      &:disabled {
        background: #f8fafc;
        color: #52525b;
        cursor: default;
      }
    }
  }

  &__field-error {
    color: #dc2626;
    font-size: 12px;
    line-height: 16px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 190px;
    min-height: 48px;
    padding: 0 18px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &--secondary {
      background: #fff;
      color: #0a77ff;
      border: 1px solid #cbd5e1;
    }

    &--primary {
      background: #fb923c;
      color: #fff;
      border: 0;
    }
  }

  @media (max-width: 960px) {
    &__grid {
      grid-template-columns: 1fr;
    }

    &__group--wide,
    &__field--wide {
      grid-column: span 1;
    }
  }

  @media (max-width: 640px) {
    &__card {
      padding: 16px;
    }

    &__actions {
      flex-direction: column;
      align-items: stretch;
    }

    &__action {
      width: 100%;
    }
  }
}
</style>
